import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { businessDebtData } from '../data/debts';
import { BusinessDebt, PaymentHistory } from '../types';
import { DollarSign, Plus, Edit, Trash2, Check, X, Download, Filter, SortAsc, SortDesc, AlertCircle, Bell, Printer, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface BusinessDebtsProps {
  companyId: string | null;
}

const BusinessDebts: React.FC<BusinessDebtsProps> = ({ companyId }) => {
  const [debts, setDebts] = useState<BusinessDebt[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [selectedDebt, setSelectedDebt] = useState<BusinessDebt | null>(null);
  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Partial<PaymentHistory>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingDebt, setEditingDebt] = useState<Partial<BusinessDebt>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [selectedDebt]);

  const loadData = async () => {
    try {
      if (!companyId) {
        setDebts([]);
        return;
      }

      // Load debts from Supabase
      const { data: debtsData, error: debtsError } = await supabase
        .from('company_debts')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (debtsError) throw debtsError;
      setDebts(debtsData || []);

      // Load payment history if a debt is selected
      if (selectedDebt) {
        const { data: historyData, error: historyError } = await supabase
          .from('payment_history')
          .select('*')
          .eq('debt_id', selectedDebt.id)
          .order('payment_date', { ascending: false });

        if (historyError) throw historyError;
        setPaymentHistory(historyData || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    }
  };

  const handleSavePayment = async () => {
    try {
      if (!editingPayment.payment_date || !editingPayment.principal_amount || !editingPayment.interest_amount) {
        setError('Please fill in all required fields');
        return;
      }

      const totalAmount = Number(editingPayment.principal_amount) + Number(editingPayment.interest_amount);

      if (editingPayment.id) {
        // Update existing payment
        const { error } = await supabase
          .from('payment_history')
          .update({
            payment_date: editingPayment.payment_date,
            principal_amount: editingPayment.principal_amount,
            interest_amount: editingPayment.interest_amount,
            total_amount: totalAmount,
            notes: editingPayment.notes
          })
          .eq('id', editingPayment.id);

        if (error) throw error;
      } else {
        // Add new payment
        const { error } = await supabase
          .from('payment_history')
          .insert({
            debt_id: selectedDebt?.id,
            payment_date: editingPayment.payment_date,
            principal_amount: editingPayment.principal_amount,
            interest_amount: editingPayment.interest_amount,
            total_amount: totalAmount,
            notes: editingPayment.notes,
            status: 'paid'
          });

        if (error) throw error;
      }

      await loadData();
      setIsEditingPayment(false);
      setEditingPayment({});
      setError(null);
    } catch (err) {
      console.error('Error saving payment:', err);
      setError(err.message);
    }
  };

  const handleSaveDebt = async () => {
    try {
      if (!companyId) {
        setError('Please select a company first');
        return;
      }

      if (!editingDebt.name || !editingDebt.current_balance || !editingDebt.payment_amount) {
        setError('Please fill in all required fields');
        return;
      }

      if (editingDebt.id) {
        // Update existing debt
        const { error } = await supabase
          .from('company_debts')
          .update({
            name: editingDebt.name,
            lender: editingDebt.lender,
            debt_type: editingDebt.debt_type,
            current_balance: editingDebt.current_balance,
            payment_amount: editingDebt.payment_amount,
            interest_rate: editingDebt.interest_rate,
            next_payment_date: editingDebt.next_payment_date,
            is_revolving: editingDebt.is_revolving,
            is_secured: editingDebt.is_secured
          })
          .eq('id', editingDebt.id);

        if (error) throw error;
      } else {
        // Add new debt
        const { error } = await supabase
          .from('company_debts')
          .insert({
            company_id: companyId,
            name: editingDebt.name,
            lender: editingDebt.lender || '',
            debt_type: editingDebt.debt_type || 'term_loan',
            original_amount: editingDebt.current_balance,
            current_balance: editingDebt.current_balance,
            interest_rate: editingDebt.interest_rate || 0,
            start_date: editingDebt.start_date || format(new Date(), 'yyyy-MM-dd'),
            maturity_date: editingDebt.maturity_date || format(new Date(), 'yyyy-MM-dd'),
            payment_amount: editingDebt.payment_amount,
            payment_frequency: editingDebt.payment_frequency || 'monthly',
            next_payment_date: editingDebt.next_payment_date || format(new Date(), 'yyyy-MM-dd'),
            is_revolving: false,
            is_secured: false
          });

        if (error) throw error;
      }

      await loadData();
      setIsEditing(false);
      setEditingDebt({});
      setError(null);
    } catch (err) {
      console.error('Error saving debt:', err);
      setError(err.message);
    }
  };

  const handleDeleteDebt = async (id: number) => {
    try {
      const { error } = await supabase
        .from('company_debts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadData();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting debt:', err);
      setError(err.message);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Business Debts</h1>
        <button
          onClick={() => {
            setEditingDebt({});
            setIsEditing(true);
          }}
          className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Debt
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Loan Paybacks Summary */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold mb-4">Upcoming Loan Payments</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-[#ffead5]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Loan</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Payment Amount</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {debts.sort((a, b) => 
                new Date(a.next_payment_date).getTime() - new Date(b.next_payment_date).getTime()
              ).map(debt => (
                <tr key={debt.id}>
                  <td className="px-6 py-4">{format(new Date(debt.next_payment_date), 'MMM d, yyyy')}</td>
                  <td className="px-6 py-4 font-medium">{debt.name}</td>
                  <td className="px-6 py-4">${debt.payment_amount.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    {new Date(debt.next_payment_date) < new Date() ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Overdue
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Upcoming
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-[#ffead5]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Interest Rate</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Next Due</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {debts.map(debt => (
                <tr key={debt.id}>
                  <td className="px-6 py-4 font-medium">{debt.name}</td>
                  <td className="px-6 py-4">${debt.current_balance.toLocaleString()}</td>
                  <td className="px-6 py-4">{debt.interest_rate}%</td>
                  <td className="px-6 py-4">${debt.payment_amount.toLocaleString()}</td>
                  <td className="px-6 py-4">{format(new Date(debt.next_payment_date), 'MMM d, yyyy')}</td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedDebt(debt);
                          setIsViewingHistory(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Payment History"
                      >
                        <Calendar className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingDebt(debt);
                          setIsEditing(true);
                        }}
                        className="text-[#ff8200] hover:text-[#e67600]"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      {showDeleteConfirm === debt.id ? (
                        <>
                          <button
                            onClick={() => handleDeleteDebt(debt.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Check className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setShowDeleteConfirm(debt.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Debt Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#ff8200]">
                {editingDebt.id ? 'Edit Debt' : 'Add New Debt'}
              </h2>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditingDebt({});
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name*
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                  value={editingDebt.name || ''}
                  onChange={e => setEditingDebt({ ...editingDebt, name: e.target.value })}
                  placeholder="e.g., Business Loan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Balance*
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                    value={editingDebt.current_balance || ''}
                    onChange={e => setEditingDebt({ ...editingDebt, current_balance: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interest Rate (%)*
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                  value={editingDebt.interest_rate || ''}
                  onChange={e => setEditingDebt({ ...editingDebt, interest_rate: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount*
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                    value={editingDebt.payment_amount || ''}
                    onChange={e => setEditingDebt({ ...editingDebt, payment_amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Next Payment Date*
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                  value={editingDebt.next_payment_date || format(new Date(), 'yyyy-MM-dd')}
                  onChange={e => setEditingDebt({ ...editingDebt, next_payment_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditingDebt({});
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDebt}
                className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {isViewingHistory && selectedDebt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#ff8200]">
                Payment History - {selectedDebt.name}
              </h2>
              <button
                onClick={() => {
                  setIsViewingHistory(false);
                  setSelectedDebt(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <button
                onClick={() => {
                  setEditingPayment({
                    payment_date: format(new Date(), 'yyyy-MM-dd'),
                    principal_amount: 0,
                    interest_amount: 0,
                    total_amount: 0
                  });
                  setIsEditingPayment(true);
                }}
                className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg flex items-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Payment
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#ffead5]">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Principal</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Interest</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Notes</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paymentHistory.map(payment => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4">{format(new Date(payment.payment_date), 'MMM d, yyyy')}</td>
                      <td className="px-6 py-4">${payment.principal_amount.toLocaleString()}</td>
                      <td className="px-6 py-4">${payment.interest_amount.toLocaleString()}</td>
                      <td className="px-6 py-4">${payment.total_amount.toLocaleString()}</td>
                      <td className="px-6 py-4">{payment.notes}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => {
                            setEditingPayment(payment);
                            setIsEditingPayment(true);
                          }}
                          className="text-[#ff8200] hover:text-[#e67600]"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {isEditingPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#ff8200]">
                {editingPayment.id ? 'Edit Payment' : 'Add Payment'}
              </h2>
              <button
                onClick={() => {
                  setIsEditingPayment(false);
                  setEditingPayment({});
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date*
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                  value={editingPayment.payment_date || ''}
                  onChange={e => setEditingPayment({ ...editingPayment, payment_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Principal Amount*
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                    value={editingPayment.principal_amount || ''}
                    onChange={e => setEditingPayment({ ...editingPayment, principal_amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interest Amount*
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                    value={editingPayment.interest_amount || ''}
                    onChange={e => setEditingPayment({ ...editingPayment, interest_amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                  value={editingPayment.notes || ''}
                  onChange={e => setEditingPayment({ ...editingPayment, notes: e.target.value })}
                  rows={3}
                  placeholder="Add any notes about this payment..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsEditingPayment(false);
                  setEditingPayment({});
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePayment}
                className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDebts;